import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import 'react-toastify/dist/ReactToastify.css';
import { Login } from './pages/login/Login';
import { EsqueceuSenha } from './pages/login/EsqueceuSenha';
import { RedefinirSenha } from './pages/login/RedefinirSenha';
import { Home } from './pages/home/Home';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UsuariosLista from './pages/usuarios/UsuariosLista'
import UsuarioId from './pages/usuarios/UsuarioId'
import UsuarioHistorico from './pages/usuarios/UsuarioHistorico'
import MontadorasLista from './pages/montadoras/MontadorasLista'
import MontadoraId from './pages/montadoras/MontadoraId'
import FontesLista from './pages/fontes/FontesLista'
import FonteId from './pages/fontes/FonteId'
import ImprimirChassi from './pages/chassi/ImprimirChassi'
import Matricial from './pages/chassi/Matricial'
import Termica from './pages/chassi/Termica'
import { useEffect } from 'react';
import { SaldoProvider } from './contexts/SaldoContext';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      // Verifica se o usuário está tentando acessar a página home
      if (window.location.pathname === '/home') {
        // Se for usuario, redireciona para imprimir-chassi
        if (user.tipo === 'usuario') {
          navigate('/imprimir-chassi');
        }
      }
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  // Limpar qualquer sessão antiga que possa estar no localStorage
  useEffect(() => {
    // Identificar e remover itens relacionados ao Supabase no localStorage.
    // O filtro antigo procurava por 'supabase.auth', formato do supabase-js v1;
    // no v2 as chaves são 'sb-<ref>-auth-token', então nada era removido.
    Object.keys(localStorage).forEach(key => {
      if (key.includes('supabase.auth') || (key.startsWith('sb-') && key.includes('-auth-token'))) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SaldoProvider>
          <BrowserRouter basename="/">
            <ToastContainer
              position="top-center"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/esqueceu-senha" element={<EsqueceuSenha />} />
              <Route path="/redefinir-senha" element={<RedefinirSenha />} />
              <Route
                path="/home"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Home />
                    </Layout>
                  </PrivateRoute>
                }
              />
              {/* Corrigindo as rotas de usuários para usar PrivateRoute */}
              <Route 
                path="/usuarios" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <UsuariosLista />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/usuarios/novo" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <UsuarioId />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/usuarios/:id" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <UsuarioId />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/usuarios/:id/historico" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <UsuarioHistorico />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/montadoras" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <MontadorasLista />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/montadoras/novo" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <MontadoraId />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/montadoras/:id" 
                element={
                  <PrivateRoute>
                    <Layout>
                      <MontadoraId />
                    </Layout>
                  </PrivateRoute>
                } 
              />
              <Route
                path="/fontes"
                element={
                  <PrivateRoute>
                    <Layout>
                      <FontesLista />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/fontes/novo"
                element={
                  <PrivateRoute>
                    <Layout>
                      <FonteId />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/fontes/:id"
                element={
                  <PrivateRoute>
                    <Layout>
                      <FonteId />
                    </Layout>
                  </PrivateRoute>
                }
              />  
              <Route
                path="/imprimir-chassi"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ImprimirChassi />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route path="/chassi/matricial" element={<Matricial />} />
              <Route path="/chassi/termica" element={<Termica />} />
            </Routes>
          </BrowserRouter>
        </SaldoProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;